import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Modal } from '../components/Modal';
import { useEvent } from '../hooks/useEvent';
import {
  createRoom,
  updateRoom,
  deleteRoom,
  reorderRooms,
  fetchTimeBlocks,
  createTimeBlock,
  deleteTimeBlock,
  createSession,
  updateSession,
  deleteSession,
  publishSchedule,
  archiveEvent,
  updateScheduleItem,
  deleteScheduleItem,
  generateSchedule,
  updateTemplate,
  fetchTemplate,
  updateEvent,
  toggleVoting,
  setActiveEvent as apiSetActive,
  deactivateEvent as apiDeactivate,
  fetchAttendees,
  mergeAttendees,
  renameTemplateOption,
} from '../lib/api';
import type { Attendee } from '../lib/api';
import { BoardGrid } from '../components/BoardGrid';
import { HandIcon } from '../components/InterestCard';
import { TemplateFields } from '../components/TemplateFields';
import type { Room, Timeslot, TimeBlock, Proposal, Session, ScheduleItem, RoomFormData, Template } from '../types';

type AdminTab = 'rooms' | 'timeslots' | 'sessions' | 'schedule' | 'attendees' | 'settings';

/** Max number of consecutive slots from startIndex that share the same block (no break in between). */
function maxSpanFromSlot(timeslots: Timeslot[], startIndex: number): number {
  const start = timeslots[startIndex];
  if (!start || start.isBreak) return 1;
  const blockId = start.blockId ?? null;
  let span = 1;
  for (let i = startIndex + 1; i < timeslots.length; i++) {
    const s = timeslots[i];
    if (s.isBreak) break;
    if ((s.blockId ?? null) !== blockId) break;
    span++;
  }
  return span;
}

export function AdminPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const {
    event,
    rooms,
    timeslots,
    proposals,
    sessions,
    scheduleItems,
    template,
    isLoading,
    isOrganizer,
    deviceId,
    nickname: currentNickname,
    refresh,
  } = useEvent(eventId ?? null);

  const [activeTab, setActiveTab] = useState<AdminTab>('rooms');

  if (isLoading) {
    return (
      <Layout title={event?.name} eventId={eventId} isOrganizer={isOrganizer} nickname={currentNickname} deviceId={deviceId}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block w-8 h-8 border-4 border-primary-200 border-t-primary-400 rounded-full animate-spin" />
            <p className="mt-2 text-gray-500">Loading...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!isOrganizer) {
    return <Navigate to={`/event/${eventId}`} replace />;
  }

  const tabs: { id: AdminTab; label: string }[] = [
    { id: 'rooms', label: 'Rooms' },
    { id: 'timeslots', label: 'Time blocks' },
    { id: 'sessions', label: 'Sessions' },
    { id: 'schedule', label: 'Schedule' },
    { id: 'attendees', label: 'Attendees' },
    { id: 'settings', label: 'Settings' },
  ];

  return (
    <Layout title={event?.name} eventId={eventId} isOrganizer={isOrganizer} nickname={currentNickname} deviceId={deviceId}>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Admin Panel</h1>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex gap-4 -mb-px" aria-label="Admin sections">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                aria-current={activeTab === tab.id ? 'page' : undefined}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab content */}
        {activeTab === 'rooms' && (
          <RoomsTab eventId={eventId!} rooms={rooms} template={template} onRefresh={refresh} />
        )}
        {activeTab === 'timeslots' && (
          <TimeslotsTab eventId={eventId!} event={event!} timeslots={timeslots} onRefresh={refresh} />
        )}
        {activeTab === 'sessions' && (
          <SessionsTab
            eventId={eventId!}
            event={event!}
            timeslots={timeslots}
            proposals={proposals}
            sessions={sessions}
            template={template}
            onRefresh={refresh}
          />
        )}
        {activeTab === 'schedule' && (
          <ScheduleTab
            eventId={eventId!}
            rooms={rooms}
            timeslots={timeslots}
            sessions={sessions}
            scheduleItems={scheduleItems}
            onRefresh={refresh}
          />
        )}
        {activeTab === 'attendees' && (
          <AttendeesTab eventId={eventId!} />
        )}
        {activeTab === 'settings' && (
          <SettingsTab eventId={eventId!} event={event!} template={template} onRefresh={refresh} />
        )}
      </div>
    </Layout>
  );
}

// ============================================
// Rooms Tab
// ============================================

function RoomsTab({
  eventId,
  rooms,
  template,
  onRefresh,
}: {
  eventId: string;
  rooms: Room[];
  template: Template | null;
  onRefresh: () => void;
}) {
  const [showModal, setShowModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleSave = async (data: RoomFormData) => {
    if (editingRoom) {
      await updateRoom(eventId, editingRoom.id, data);
    } else {
      await createRoom(eventId, data);
    }
    onRefresh();
    setShowModal(false);
    setEditingRoom(null);
  };

  const handleDelete = async (roomId: string) => {
    if (confirm('Are you sure you want to delete this room?')) {
      await deleteRoom(eventId, roomId);
      onRefresh();
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    e.dataTransfer.setData('application/json', JSON.stringify({ index }));
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setDragOverIndex(null);
    setDraggedIndex(null);
    const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (Number.isNaN(fromIndex) || fromIndex === dropIndex) return;
    const newOrder = [...rooms];
    const [removed] = newOrder.splice(fromIndex, 1);
    newOrder.splice(dropIndex, 0, removed);
    await reorderRooms(eventId, newOrder.map((r) => r.id));
    onRefresh();
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Rooms</h2>
        <button onClick={() => setShowModal(true)} className="btn btn-primary">
          Add Room
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-2">Drag and drop to reorder rooms.</p>

      {rooms.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No rooms configured yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rooms.map((room, index) => (
            <div
              key={room.id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              className={`card flex items-center justify-between cursor-grab active:cursor-grabbing ${
                draggedIndex === index ? 'opacity-50' : ''
              } ${dragOverIndex === index ? 'ring-2 ring-primary-300' : ''}`}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-gray-400 select-none" aria-hidden>
                  ⋮⋮
                </span>
                <div className="min-w-0">
                  <h3 className="font-medium">{room.name}</h3>
                  {room.sizeText && (
                    <p className="text-sm text-gray-500">{room.sizeText}</p>
                  )}
                  {room.locationNotes && (
                    <p className="text-sm text-gray-400">{room.locationNotes}</p>
                  )}
                  {((room.preferredFormats && room.preferredFormats.length > 0) ||
                    (room.preferredTags && room.preferredTags.length > 0)) && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {room.preferredFormats?.map((f) => (
                        <span key={f} className="badge badge-primary text-[10px]">{f}</span>
                      ))}
                      {room.preferredTags?.map((t) => (
                        <span key={t} className="badge badge-yellow text-[10px]">{t}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => {
                    setEditingRoom(room);
                    setShowModal(true);
                  }}
                  className="btn btn-secondary btn-sm"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(room.id)}
                  className="btn btn-ghost btn-sm text-red-600 hover:text-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <RoomModal
        isOpen={showModal}
        room={editingRoom}
        template={template}
        onClose={() => {
          setShowModal(false);
          setEditingRoom(null);
        }}
        onSave={handleSave}
      />
    </div>
  );
}

function RoomModal({
  isOpen,
  room,
  template,
  onClose,
  onSave,
}: {
  isOpen: boolean;
  room: Room | null;
  template: Template | null;
  onClose: () => void;
  onSave: (data: RoomFormData) => Promise<void>;
}) {
  const [formData, setFormData] = useState<RoomFormData>({
    name: room?.name ?? '',
    sizeText: room?.sizeText ?? '',
    locationNotes: room?.locationNotes ?? '',
    preferredFormats: room?.preferredFormats ?? [],
    preferredTags: room?.preferredTags ?? [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  React.useEffect(() => {
    if (room) {
      setFormData({
        name: room.name,
        sizeText: room.sizeText ?? '',
        locationNotes: room.locationNotes ?? '',
        preferredFormats: room.preferredFormats ?? [],
        preferredTags: room.preferredTags ?? [],
      });
    } else {
      setFormData({ name: '', sizeText: '', locationNotes: '', preferredFormats: [], preferredTags: [] });
    }
  }, [room]);

  const formatOptions = template?.formatOptions ?? [];
  const tagOptions = template?.tagOptions ?? [];

  const toggleInArray = (arr: string[], value: string) =>
    arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSave(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={room ? 'Edit Room' : 'Add Room'}
      footer={
        <>
          <button onClick={onClose} className="btn btn-secondary" disabled={isSubmitting}>
            Cancel
          </button>
          <button
            type="submit"
            form="room-form"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </button>
        </>
      }
    >
      <form id="room-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="roomName" className="label">
            Room Name *
          </label>
          <input
            id="roomName"
            type="text"
            className="input"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            autoFocus
          />
        </div>

        <div>
          <label htmlFor="sizeText" className="label">
            Capacity (informational)
          </label>
          <input
            id="sizeText"
            type="text"
            className="input"
            value={formData.sizeText}
            onChange={(e) => setFormData({ ...formData, sizeText: e.target.value })}
            placeholder="e.g., 30 seats"
          />
        </div>

        <div>
          <label htmlFor="locationNotes" className="label">
            Location Notes
          </label>
          <textarea
            id="locationNotes"
            className="input"
            value={formData.locationNotes}
            onChange={(e) => setFormData({ ...formData, locationNotes: e.target.value })}
            placeholder="e.g., 2nd floor, next to cafeteria"
          />
        </div>

        {/* ---- Auto-schedule preferences ---- */}
        {(formatOptions.length > 0 || tagOptions.length > 0) && (
          <div className="pt-2" style={{ borderTop: '1px solid rgba(0,0,0,0.08)' }}>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Auto-Schedule Preferences
            </p>

            {formatOptions.length > 0 && (
              <div className="mb-3">
                <label className="label">Allowed Formats</label>
                <p className="text-xs text-gray-400 mb-1.5">
                  Only sessions with these formats will be assigned to this room. Leave empty for any format.
                </p>
                <div className="flex flex-wrap gap-2">
                  {formatOptions.map((fmt) => {
                    const selected = formData.preferredFormats?.includes(fmt);
                    return (
                      <button
                        key={fmt}
                        type="button"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            preferredFormats: toggleInArray(formData.preferredFormats ?? [], fmt),
                          })
                        }
                        className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                          selected
                            ? 'bg-primary-100 text-primary-800 border-primary-300'
                            : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {fmt}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {tagOptions.length > 0 && (
              <div>
                <label className="label">Preferred Tags</label>
                <p className="text-xs text-gray-400 mb-1.5">
                  Sessions with these tags are preferred for this room. Leave empty for no preference.
                </p>
                <div className="flex flex-wrap gap-2">
                  {tagOptions.map((tag) => {
                    const selected = formData.preferredTags?.includes(tag);
                    return (
                      <button
                        key={tag}
                        type="button"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            preferredTags: toggleInArray(formData.preferredTags ?? [], tag),
                          })
                        }
                        className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                          selected
                            ? 'bg-primary-100 text-primary-800 border-primary-300'
                            : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {tag}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </form>
    </Modal>
  );
}

// ============================================
// Timeslots Tab
// ============================================

function TimeslotsTab({
  eventId,
  event,
  timeslots,
  onRefresh,
}: {
  eventId: string;
  event: { defaultSlotDurationMinutes?: number };
  timeslots: Timeslot[];
  onRefresh: () => void;
}) {
  const defaultSlotMinutes = event?.defaultSlotDurationMinutes ?? 15;
  const [timeBlocks, setTimeBlocks] = useState<TimeBlock[]>([]);
  const [showCreateBlockModal, setShowCreateBlockModal] = useState(false);

  useEffect(() => {
    fetchTimeBlocks(eventId).then(setTimeBlocks).catch(() => setTimeBlocks([]));
  }, [eventId, timeslots.length]);

  const handleCreateBlock = async (data: { blockDate: string; startTime: string; slotCount: number; label?: string }) => {
    const [sh, sm] = data.startTime.split(':').map(Number);
    const startMinutes = sh * 60 + sm;
    const endMinutes = startMinutes + data.slotCount * defaultSlotMinutes;
    const endH = Math.floor(endMinutes / 60) % 24;
    const endM = endMinutes % 60;
    const endTimeStr = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
    const startTs = `${data.blockDate}T${data.startTime}:00`;
    const endTs = `${data.blockDate}T${endTimeStr}:00`;
    await createTimeBlock(eventId, { startTs, endTs, slotDurationMinutes: defaultSlotMinutes, label: data.label || undefined });
    setShowCreateBlockModal(false);
    const list = await fetchTimeBlocks(eventId);
    setTimeBlocks(list);
    onRefresh();
  };

  const handleDeleteBlock = async (blockId: string) => {
    if (confirm('Delete this time block and all its slots?')) {
      await deleteTimeBlock(eventId, blockId);
      setTimeBlocks((prev) => prev.filter((b) => b.id !== blockId));
      onRefresh();
    }
  };

  const formatBlockTime = (start: string, end: string) => {
    const fmt = (d: string) => new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return `${fmt(start)} – ${fmt(end)}`;
  };

  return (
    <div className="space-y-8">
      {/* Time blocks */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Time Blocks</h2>
          <button onClick={() => setShowCreateBlockModal(true)} className="btn btn-primary">
            Add time block
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-3">
          Define a time range (e.g. 11:00–11:45) and split it into equal slots (e.g. 3×15 min). Sessions can span 1 or more slots within a block.
        </p>
        {timeBlocks.length === 0 ? (
          <div className="text-center py-6 bg-gray-50 rounded-lg">
            <p className="text-gray-500">No time blocks. Add one to generate slots from a time range.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {timeBlocks.map((block) => (
              <div key={block.id} className="card flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-mono">{formatBlockTime(block.startTs, block.endTs)}</span>
                  <span className="text-gray-500">{block.slotDurationMinutes} min slots</span>
                  {block.label && <span className="text-gray-600">{block.label}</span>}
                </div>
                <button
                  onClick={() => handleDeleteBlock(block.id)}
                  className="btn btn-ghost btn-sm text-red-600 hover:text-red-700"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
        <CreateTimeBlockModal
          isOpen={showCreateBlockModal}
          onClose={() => setShowCreateBlockModal(false)}
          defaultSlotMinutes={defaultSlotMinutes}
          onCreate={handleCreateBlock}
        />
      </div>
    </div>
  );
}

function CreateTimeBlockModal({
  isOpen,
  onClose,
  defaultSlotMinutes,
  onCreate,
}: {
  isOpen: boolean;
  onClose: () => void;
  defaultSlotMinutes: number;
  onCreate: (data: { blockDate: string; startTime: string; slotCount: number; label?: string }) => Promise<void>;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [blockDate, setBlockDate] = useState(today);
  const [startTime, setStartTime] = useState('09:00');
  const [slotCount, setSlotCount] = useState(3);
  const [label, setLabel] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onCreate({ blockDate, startTime, slotCount, label: label.trim() || undefined });
      setBlockDate(today);
      setStartTime('09:00');
      setSlotCount(3);
      setLabel('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const endMinutes = (() => {
    const [sh, sm] = startTime.split(':').map(Number);
    return sh * 60 + sm + slotCount * defaultSlotMinutes;
  })();
  const endTimeStr = `${String(Math.floor(endMinutes / 60) % 24).padStart(2, '0')}:${String(endMinutes % 60).padStart(2, '0')}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add time block"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary" disabled={isSubmitting}>Cancel</button>
          <button type="submit" form="create-time-block-form" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create'}
          </button>
        </>
      }
    >
      <form id="create-time-block-form" onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-500">
          Slot length is {defaultSlotMinutes} min (set in Settings). This block will have {slotCount} slot{slotCount !== 1 ? 's' : ''} → {startTime} until {endTimeStr}.
        </p>
        <div>
          <label htmlFor="block-date" className="label">Date</label>
          <input
            id="block-date"
            type="date"
            className="input"
            value={blockDate}
            onChange={(e) => setBlockDate(e.target.value)}
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="block-start" className="label">Start time</label>
            <input
              id="block-start"
              type="time"
              className="input"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="slot-count" className="label">Number of slots</label>
            <select
              id="slot-count"
              className="select"
              value={slotCount}
              onChange={(e) => setSlotCount(Number(e.target.value))}
            >
              {[1, 2, 3, 4, 5, 6, 8, 10, 12].map((n) => (
                <option key={n} value={n}>{n} slot{n !== 1 ? 's' : ''} ({n * defaultSlotMinutes} min)</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label htmlFor="block-label" className="label">Label (optional)</label>
          <input
            id="block-label"
            type="text"
            className="input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Morning block"
          />
        </div>
      </form>
    </Modal>
  );
}

// ============================================
// Sessions Tab
// ============================================

function SessionsTab({
  eventId,
  event,
  timeslots,
  proposals,
  sessions,
  template,
  onRefresh,
}: {
  eventId: string;
  event: { defaultSlotDurationMinutes?: number; presentingProposalId?: string | null };
  timeslots: Timeslot[];
  proposals: Proposal[];
  sessions: Session[];
  template: Template | null;
  onRefresh: () => void;
}) {
  const defaultSlotMinutes = event?.defaultSlotDurationMinutes ?? 15;
  const slotSpanOptions = React.useMemo(() => {
    const blocks = new Map<string, number>();
    timeslots.filter((t) => !t.isBreak).forEach((t) => {
      const k = t.blockId ?? t.id;
      blocks.set(k, (blocks.get(k) ?? 0) + 1);
    });
    const max = Math.max(0, ...blocks.values(), 1);
    return Array.from({ length: Math.min(max, 12) }, (_, i) => i + 1);
  }, [timeslots]);

  const [selectedProposals, setSelectedProposals] = useState<string[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSession, setEditingSession] = useState<Session | null>(null);
  const [creatingDirectly, setCreatingDirectly] = useState<string | null>(null);

  const sessionProposalIds = new Set(sessions.flatMap((s) => s.proposals.map((p) => p.id)));
  const unassignedProposals = proposals.filter((p) => !sessionProposalIds.has(p.id));

  // The currently-presented proposal id (if it's unassigned)
  const presentingId = event?.presentingProposalId ?? null;
  const isPresentingUnassigned = presentingId && unassignedProposals.some((p) => p.id === presentingId);

  // Auto-select the presented card when it becomes the presenting proposal
  React.useEffect(() => {
    if (isPresentingUnassigned && presentingId && !selectedProposals.includes(presentingId)) {
      setSelectedProposals((prev) =>
        prev.includes(presentingId) ? prev : [presentingId, ...prev]
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presentingId, isPresentingUnassigned]);

  /** Direct single-click session creation from one session card (no modal). */
  const handleDirectCreateSession = async (proposal: Proposal) => {
    setCreatingDirectly(proposal.id);
    try {
      await createSession(eventId, {
        title: proposal.title,
        description: proposal.description || undefined,
        fields: proposal.fields && Object.keys(proposal.fields).length ? proposal.fields : undefined,
        proposalIds: [proposal.id],
        people: (proposal.people?.length
          ? proposal.people.map((p) => ({ nickname: p.nickname, role: p.role || 'presenter' }))
          : [{ nickname: proposal.createdByNickname, role: 'presenter' }]),
        spanSlots: proposal.preferredSlotSpan ?? undefined,
      });
      onRefresh();
    } catch (e) {
      console.error('Failed to create session:', e);
    } finally {
      setCreatingDirectly(null);
    }
  };

  /** Multi-card combine via modal. */
  const handleCreateSession = async (
    title: string,
    description: string,
    fields?: Record<string, string>,
    spanSlots?: number | null,
    people?: Array<{ nickname: string; role: string }>
  ) => {
    const singleProposal = selectedProposals.length === 1 ? proposals.find((p) => p.id === selectedProposals[0]) : null;
    const finalPeople = people ?? selectedProposals.map((id) => {
      const p = proposals.find((prop) => prop.id === id);
      return { nickname: p?.createdByNickname ?? '', role: 'presenter' as const };
    });
    const finalSpan = spanSlots ?? singleProposal?.preferredSlotSpan ?? undefined;
    await createSession(eventId, {
      title,
      description: description || undefined,
      fields: fields && Object.keys(fields).length ? fields : undefined,
      proposalIds: selectedProposals,
      people: finalPeople,
      spanSlots: finalSpan,
    });
    setSelectedProposals([]);
    onRefresh();
    setShowCreateModal(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Sessions</h2>
        <p className="text-sm text-gray-500">
          {sessions.length} sessions from {proposals.length} session cards
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Unassigned proposals */}
        <div>
          <h3 className="font-medium mb-2">Unassigned Session Cards</h3>
          {unassignedProposals.length === 0 ? (
            <p className="text-sm text-gray-500">All session cards have been assigned.</p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {unassignedProposals.map((proposal) => {
                const isPresenting = proposal.id === presentingId;
                return (
                <label
                  key={proposal.id}
                  className={`card cursor-pointer flex items-start gap-3 ${
                    selectedProposals.includes(proposal.id)
                      ? 'ring-2 ring-primary-500'
                      : ''
                  } ${isPresenting ? 'border-2 border-yellow-400 bg-yellow-50' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedProposals.includes(proposal.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedProposals([...selectedProposals, proposal.id]);
                      } else {
                        setSelectedProposals(
                          selectedProposals.filter((id) => id !== proposal.id)
                        );
                      }
                    }}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    {isPresenting && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-yellow-700 bg-yellow-200 px-1.5 py-0.5 rounded mb-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                        NOW PRESENTING
                      </span>
                    )}
                    <p className="font-medium line-clamp-1">{proposal.title}</p>
                    <p className="text-sm text-gray-500">{proposal.createdByNickname}</p>
                    <p className="text-sm text-gray-400 flex items-center gap-1"><HandIcon className="w-3.5 h-3.5 inline" /> {proposal.voteCount} interested</p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      handleDirectCreateSession(proposal);
                    }}
                    className="btn btn-primary btn-sm flex-shrink-0"
                    disabled={creatingDirectly === proposal.id}
                  >
                    {creatingDirectly === proposal.id ? 'Creating...' : 'Create session'}
                  </button>
                </label>
                );
              })}
            </div>
          )}

          {selectedProposals.length > 0 && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn btn-primary mt-4"
            >
              {selectedProposals.length === 1
                ? 'Create Session (1 card selected)'
                : `Combine ${selectedProposals.length} Cards into Session`}
            </button>
          )}
        </div>

        {/* Existing sessions */}
        <div>
          <h3 className="font-medium mb-2">Sessions</h3>
          {sessions.length === 0 ? (
            <p className="text-sm text-gray-500">No sessions created yet.</p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {sessions.map((session) => (
                <div key={session.id} className="card flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{session.title}</p>
                    <p className="text-sm text-gray-500">
                      {session.proposals.length} session card{session.proposals.length !== 1 ? 's' : ''} combined
                      {session.proposalVoteCount != null && session.proposalVoteCount > 0 && (
                        <> · <HandIcon className="w-3.5 h-3.5 inline" /> {session.proposalVoteCount} interested</>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setEditingSession(session)}
                      className="btn btn-secondary btn-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={async () => {
                        if (confirm('Remove this session? Session cards will become unassigned again.')) {
                          await deleteSession(eventId, session.id);
                          onRefresh();
                        }
                      }}
                      className="btn btn-ghost btn-sm text-red-600 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <CreateSessionModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        selectedProposals={proposals.filter((p) => selectedProposals.includes(p.id))}
        slotSpanOptions={slotSpanOptions}
        defaultSlotMinutes={defaultSlotMinutes}
        template={template}
        presentingProposalId={presentingId}
        onCreate={handleCreateSession}
      />
      {editingSession && (
        <EditSessionModal
          session={editingSession}
          eventId={eventId}
          proposals={proposals}
          sessions={sessions}
          defaultSlotMinutes={defaultSlotMinutes}
          slotSpanOptions={slotSpanOptions}
          template={template}
          onClose={() => setEditingSession(null)}
          onSave={async () => {
            onRefresh();
            setEditingSession(null);
          }}
        />
      )}
    </div>
  );
}

function CreateSessionModal({
  isOpen,
  onClose,
  selectedProposals,
  slotSpanOptions,
  defaultSlotMinutes,
  template,
  presentingProposalId,
  onCreate,
}: {
  isOpen: boolean;
  onClose: () => void;
  selectedProposals: Proposal[];
  slotSpanOptions: number[];
  defaultSlotMinutes: number;
  template: Template | null;
  presentingProposalId?: string | null;
  onCreate: (title: string, description: string, fields?: Record<string, string>, spanSlots?: number | null, people?: Array<{ nickname: string; role: string }>) => Promise<void>;
}) {
  const single = selectedProposals.length === 1 ? selectedProposals[0] : null;
  const defaultTitle = single ? single.title : selectedProposals.map((p) => p.title).join(' + ');
  const defaultDescription = single ? (single.description ?? '') : '';
  const defaultPeople = selectedProposals.map((p) => ({ nickname: p.createdByNickname, role: 'presenter' as const }));
  const defaultSpan = single ? (single.preferredSlotSpan ?? 1) : Math.max(1, ...selectedProposals.map((p) => p.preferredSlotSpan ?? 1).filter(Boolean));
  const defaultFields = single?.fields && Object.keys(single.fields).length ? { ...single.fields } : {};

  const [title, setTitle] = useState(defaultTitle);
  const [description, setDescription] = useState(defaultDescription);
  const [fields, setFields] = useState<Record<string, string>>(defaultFields);
  const [spanSlots, setSpanSlots] = useState(defaultSpan);
  const [people, setPeople] = useState<Array<{ nickname: string; role: string }>>(defaultPeople);
  const [newPersonName, setNewPersonName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  const proposalIdsKey = selectedProposals.map((p) => p.id).join(',');
  React.useEffect(() => {
    setTitle(defaultTitle);
    setDescription(defaultDescription);
    setFields(defaultFields);
    setSpanSlots(defaultSpan);
    setPeople(selectedProposals.map((p) => ({ nickname: p.createdByNickname, role: 'presenter' })));
    setExpandedCard(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalIdsKey]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const submitFields = Object.keys(fields).length ? fields : undefined;
      await onCreate(title, description, submitFields, spanSlots || null, people.filter((p) => p.nickname.trim()));
    } finally {
      setIsSubmitting(false);
    }
  };

  const addPerson = () => {
    const name = newPersonName.trim();
    if (name && !people.some((p) => p.nickname.trim().toLowerCase() === name.toLowerCase())) {
      setPeople([...people, { nickname: name, role: 'presenter' }]);
      setNewPersonName('');
    }
  };

  /** Copy description from a session card into the session description. */
  const useDescriptionFrom = (p: Proposal) => {
    setDescription(p.description ?? '');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={selectedProposals.length > 1 ? 'Combine Session Cards into Session' : 'Create Session'}
      footer={
        <>
          <button onClick={onClose} className="btn btn-secondary" disabled={isSubmitting}>
            Cancel
          </button>
          <button
            type="submit"
            form="create-session-form"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create Session'}
          </button>
        </>
      }
    >
      <form id="create-session-form" onSubmit={handleSubmit} className="space-y-4">
        {/* Session cards with expandable details */}
        <div>
          <label className="label">Session Cards ({selectedProposals.length})</label>
          <div className="space-y-2">
            {selectedProposals.map((p) => {
              const isPres = p.id === presentingProposalId;
              return (
              <div key={p.id} className={`border rounded-lg overflow-hidden ${isPres ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200'}`}>
                <button
                  type="button"
                  onClick={() => setExpandedCard(expandedCard === p.id ? null : p.id)}
                  className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm ${isPres ? 'hover:bg-yellow-100' : 'hover:bg-gray-50'}`}
                >
                  <span>
                    {isPres && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-yellow-700 bg-yellow-200 px-1.5 py-0.5 rounded mr-2">
                        PRESENTING
                      </span>
                    )}
                    <strong>{p.title}</strong>
                    <span className="text-gray-500 ml-2">by {p.createdByNickname}</span>
                    <span className="text-gray-400 ml-2 inline-flex items-center gap-0.5"><HandIcon className="w-3.5 h-3.5" /> {p.voteCount}</span>
                  </span>
                  <svg
                    className={`w-4 h-4 text-gray-400 transform transition-transform ${expandedCard === p.id ? 'rotate-90' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                {expandedCard === p.id && (
                  <div className="px-3 pb-3 border-t border-gray-100 bg-gray-50 space-y-2">
                    {p.description && (
                      <div className="mt-2">
                        <span className="text-xs text-gray-500 uppercase tracking-wider">Description</span>
                        <p className="text-sm whitespace-pre-wrap mt-1">{p.description}</p>
                        <button
                          type="button"
                          onClick={() => useDescriptionFrom(p)}
                          className="text-xs text-primary-600 hover:text-primary-700 mt-1"
                        >
                          Use this description for session
                        </button>
                      </div>
                    )}
                    {p.fields && Object.keys(p.fields).length > 0 && (
                      <div>
                        <span className="text-xs text-gray-500 uppercase tracking-wider">Fields</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {Object.entries(p.fields).map(([k, v]) => v && (
                            <span key={k} className="badge badge-gray text-xs">{k}: {v}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {p.preferredSlotSpan && (
                      <p className="text-xs text-gray-500">Preferred length: {p.preferredSlotSpan * defaultSlotMinutes} min</p>
                    )}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>

        <div>
          <label htmlFor="sessionTitle" className="label">
            Session Title *
          </label>
          <input
            id="sessionTitle"
            type="text"
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>

        <div>
          <label htmlFor="sessionDescription" className="label">
            Description
          </label>
          <textarea
            id="sessionDescription"
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {/* Language, Format, Audience Level, Tags */}
        <TemplateFields
          template={template}
          fields={fields}
          onChange={(name, value) => setFields({ ...fields, [name]: value })}
          idPrefix="csession-"
        />

        {slotSpanOptions.length > 0 && (
          <div>
            <label htmlFor="sessionSpanSlots" className="label">Session length</label>
            <select
              id="sessionSpanSlots"
              className="select"
              value={spanSlots}
              onChange={(e) => setSpanSlots(Number(e.target.value))}
            >
              {slotSpanOptions.map((n) => (
                <option key={n} value={n}>{n * defaultSlotMinutes} min ({n} slot{n !== 1 ? 's' : ''})</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="label">Names (presenters / contributors)</label>
          <p className="text-sm text-gray-500 mb-2">Pre-filled from session cards. Add or remove names.</p>
          <ul className="space-y-1 mb-2">
            {people.map((p, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="flex-1 text-sm">{p.nickname}</span>
                <button
                  type="button"
                  onClick={() => setPeople(people.filter((_, j) => j !== i))}
                  className="btn btn-ghost btn-sm text-red-600"
                  aria-label="Remove"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
          <div className="flex gap-2">
            <input
              type="text"
              className="input flex-1"
              placeholder="Add name"
              value={newPersonName}
              onChange={(e) => setNewPersonName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addPerson())}
            />
            <button type="button" onClick={addPerson} className="btn btn-secondary">Add</button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

function EditSessionModal({
  session,
  eventId,
  proposals,
  sessions,
  defaultSlotMinutes,
  slotSpanOptions,
  template,
  onClose,
  onSave,
}: {
  session: Session;
  eventId: string;
  proposals: Proposal[];
  sessions: Session[];
  defaultSlotMinutes: number;
  slotSpanOptions: number[];
  template: Template | null;
  onClose: () => void;
  onSave: () => void;
}) {
  const [title, setTitle] = useState(session.title);
  const [description, setDescription] = useState(session.description ?? '');
  const [spanSlots, setSpanSlots] = useState(session.spanSlots ?? 1);
  const [proposalIds, setProposalIds] = useState<string[]>(() => session.proposals?.map((p) => p.id) ?? []);
  const [people, setPeople] = useState<Array<{ nickname: string; role: string }>>(() => {
    if (session.people?.length) return session.people.map((p) => ({ nickname: p.nickname, role: p.role }));
    if (session.proposals?.length) return session.proposals.map((p) => ({ nickname: p.createdByNickname, role: 'presenter' }));
    return [];
  });
  const [fields, setFields] = useState<Record<string, string>>(() => ({ ...session.fields }));
  const [newPersonName, setNewPersonName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  React.useEffect(() => {
    setTitle(session.title);
    setDescription(session.description ?? '');
    setSpanSlots(session.spanSlots ?? 1);
    setProposalIds(session.proposals?.map((p) => p.id) ?? []);
    setPeople(
      session.people?.length
        ? session.people.map((p) => ({ nickname: p.nickname, role: p.role }))
        : session.proposals?.length
          ? session.proposals.map((p) => ({ nickname: p.createdByNickname, role: 'presenter' }))
          : []
    );
    setFields({ ...session.fields });
  }, [session.id, session.title, session.description, session.spanSlots, session.people, session.proposals, session.fields]);

  const attachedProposals = proposalIds.map((id) => proposals.find((p) => p.id === id)).filter(Boolean) as Proposal[];
  const otherSessionProposalIds = new Set(
    sessions.filter((s) => s.id !== session.id).flatMap((s) => s.proposals.map((p) => p.id))
  );
  const addableProposals = proposals.filter((p) => !proposalIds.includes(p.id) && !otherSessionProposalIds.has(p.id));

  const addPerson = () => {
    const name = newPersonName.trim();
    if (name && !people.some((p) => p.nickname.trim().toLowerCase() === name.toLowerCase())) {
      setPeople([...people, { nickname: name, role: 'presenter' }]);
      setNewPersonName('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await updateSession(eventId, session.id, {
        title,
        description: description || null,
        spanSlots: spanSlots || null,
        people: people.filter((p) => p.nickname.trim()),
        proposalIds,
        fields: Object.keys(fields).length ? fields : null,
      });
      onSave();
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Edit Session"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary" disabled={isSubmitting}>Cancel</button>
          <button type="submit" form="edit-session-form" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save'}
          </button>
        </>
      }
    >
      <form id="edit-session-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="editSessionTitle" className="label">Title *</label>
          <input
            id="editSessionTitle"
            type="text"
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>
        <div>
          <label htmlFor="editSessionDescription" className="label">Description</label>
          <textarea
            id="editSessionDescription"
            className="input min-h-[80px]"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {/* Language, Format, Audience Level, Tags */}
        <TemplateFields
          template={template}
          fields={fields}
          onChange={(name, value) => setFields({ ...fields, [name]: value })}
          idPrefix="esession-"
        />

        <div>
          <label className="label">Attached session cards</label>
          <p className="text-sm text-gray-500 mb-2">Session cards combined into this session. Add or remove below.</p>
          {attachedProposals.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No session cards attached.</p>
          ) : (
            <ul className="space-y-1 mb-2">
              {attachedProposals.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-2 py-1 border-b border-gray-100">
                  <span className="text-sm"><strong>{p.title}</strong> — {p.createdByNickname}</span>
                  <button
                    type="button"
                    onClick={() => setProposalIds(proposalIds.filter((id) => id !== p.id))}
                    className="btn btn-ghost btn-sm text-red-600"
                    aria-label="Remove session card"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
          {addableProposals.length > 0 && (
            <div className="flex gap-2 mt-2">
              <select
                className="select flex-1"
                value=""
                onChange={(e) => {
                  const id = e.target.value;
                  if (id) setProposalIds([...proposalIds, id]);
                  e.target.value = '';
                }}
              >
                <option value="">Add a session card…</option>
                {addableProposals.map((p) => (
                  <option key={p.id} value={p.id}>{p.title} — {p.createdByNickname}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {slotSpanOptions.length > 0 && (
          <div>
            <label htmlFor="editSessionSpan" className="label">Session length</label>
            <select
              id="editSessionSpan"
              className="select"
              value={spanSlots}
              onChange={(e) => setSpanSlots(Number(e.target.value))}
            >
              {slotSpanOptions.map((n) => (
                <option key={n} value={n}>{n * defaultSlotMinutes} min ({n} slot{n !== 1 ? 's' : ''})</option>
              ))}
            </select>
          </div>
        )}

        {template?.tagOptions && template.tagOptions.length > 0 && (
          <div>
            <label className="label">Tags</label>
            <input
              type="text"
              className="input"
              value={fields?.tags ?? ''}
              onChange={(e) => setFields({ ...fields, tags: e.target.value })}
              placeholder="Comma-separated tags"
            />
          </div>
        )}

        <div>
          <label className="label">Names (presenters / contributors)</label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              className="input flex-1"
              placeholder="Add name"
              value={newPersonName}
              onChange={(e) => setNewPersonName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addPerson())}
            />
            <button type="button" onClick={addPerson} className="btn btn-secondary">Add</button>
          </div>
          <ul className="space-y-1">
            {people.map((p, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="flex-1 text-sm">{p.nickname}</span>
                <button
                  type="button"
                  onClick={() => setPeople(people.filter((_, j) => j !== i))}
                  className="btn btn-ghost btn-sm text-red-600"
                  aria-label="Remove"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </div>
      </form>
    </Modal>
  );
}

// ============================================
// Schedule Tab
// ============================================

function ScheduleTab({
  eventId,
  rooms,
  timeslots,
  sessions,
  scheduleItems,
  onRefresh,
}: {
  eventId: string;
  rooms: Room[];
  timeslots: Timeslot[];
  sessions: Session[];
  scheduleItems: ScheduleItem[];
  onRefresh: () => void;
}) {
  const [isPublishing, setIsPublishing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [publishResult, setPublishResult] = useState<string | null>(null);
  const [assignCell, setAssignCell] = useState<{ roomId: string; timeslotIndex: number } | null>(null);
  const [removeItem, setRemoveItem] = useState<{ sessionId: string; sessionTitle: string } | null>(null);

  const handlePublish = async () => {
    if (!confirm('Publish the current schedule? This will be visible to all attendees.')) {
      return;
    }
    setIsPublishing(true);
    setPublishResult(null);
    try {
      const result = await publishSchedule(eventId);
      setPublishResult(`Published version ${result.versionNum}`);
      onRefresh();
    } catch {
      setPublishResult('Failed to publish');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleGenerate = async () => {
    if (!confirm('Auto-place unscheduled sessions into empty slots (by interest count)?')) return;
    setIsGenerating(true);
    try {
      const result = await generateSchedule(eventId);
      onRefresh();
      setPublishResult(null);
      alert(`Placed ${result.placed} session(s) into the schedule.`);
    } catch (e) {
      alert('Failed to generate schedule: ' + (e instanceof Error ? e.message : 'Unknown error'));
    } finally {
      setIsGenerating(false);
    }
  };

  const scheduledSessionIds = new Set(scheduleItems.map((i) => i.sessionId));
  const unscheduledSessions = sessions.filter((s) => !scheduledSessionIds.has(s.id));
  const nonBreakSlots = timeslots.filter((t) => !t.isBreak);
  const hasGrid = rooms.length > 0 && nonBreakSlots.length > 0;

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
        <h2 className="text-lg font-semibold">Schedule Management</h2>
        <div className="flex flex-wrap gap-2">
          {hasGrid && unscheduledSessions.length > 0 && (
            <button
              onClick={handleGenerate}
              className="btn btn-secondary"
              disabled={isGenerating}
            >
              {isGenerating ? 'Generating...' : 'Generate Schedule'}
            </button>
          )}
          <button
            onClick={handlePublish}
            className="btn btn-primary"
            disabled={isPublishing}
          >
            {isPublishing ? 'Publishing...' : 'Publish Schedule'}
          </button>
        </div>
      </div>

      {publishResult && (
        <div className="bg-green-50 text-green-700 p-4 rounded-lg mb-4">
          {publishResult}
        </div>
      )}

      {!hasGrid ? (
        <div className="bg-gray-50 rounded-lg p-6 text-center">
          <p className="text-gray-500">Add rooms and time slots first, then you can build the schedule here.</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-500 mb-2">
            Click an empty cell to assign a session, or drag a session to move it. Click a scheduled session to remove it from the slot.
          </p>
          <BoardGrid
            rooms={rooms}
            timeslots={timeslots}
            sessions={sessions}
            scheduleItems={scheduleItems}
            onCellClick={(roomId, timeslotIndex) => setAssignCell({ roomId, timeslotIndex })}
            onSessionClick={(sessionId) => {
              const session = sessions.find((s) => s.id === sessionId);
              if (session) setRemoveItem({ sessionId, sessionTitle: session.title });
            }}
            onSessionMove={async (sessionId, roomId, timeslotIndexStart, spanSlots) => {
              await updateScheduleItem(eventId, sessionId, roomId, timeslotIndexStart, spanSlots);
              onRefresh();
            }}
          />

          {assignCell && (
            <AssignSessionModal
              sessions={unscheduledSessions}
              roomId={assignCell.roomId}
              timeslotIndex={assignCell.timeslotIndex}
              maxSlots={maxSpanFromSlot(timeslots, assignCell.timeslotIndex)}
              onClose={() => setAssignCell(null)}
              onAssign={async (sessionId, spanSlots) => {
                await updateScheduleItem(eventId, sessionId, assignCell.roomId, assignCell.timeslotIndex, spanSlots);
                onRefresh();
                setAssignCell(null);
              }}
            />
          )}

          {removeItem && (
            <Modal
              isOpen={true}
              onClose={() => setRemoveItem(null)}
              title="Remove from schedule?"
              footer={
                <>
                  <button onClick={() => setRemoveItem(null)} className="btn btn-secondary">
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      await deleteScheduleItem(eventId, removeItem.sessionId);
                      onRefresh();
                      setRemoveItem(null);
                    }}
                    className="btn btn-danger"
                  >
                    Remove from slot
                  </button>
                </>
              }
            >
              <p>
                Remove &quot;{removeItem.sessionTitle}&quot; from this time slot? The session will still exist; you can place it elsewhere.
              </p>
            </Modal>
          )}
        </>
      )}
    </div>
  );
}

function AssignSessionModal({
  sessions,
  maxSlots,
  onClose,
  onAssign,
}: {
  sessions: Session[];
  roomId: string;
  timeslotIndex: number;
  maxSlots: number;
  onClose: () => void;
  onAssign: (sessionId: string, spanSlots: number) => Promise<void>;
}) {
  const [assigning, setAssigning] = useState(false);
  const [spanSlots, setSpanSlots] = useState(1);
  return (
    <Modal isOpen={true} onClose={onClose} title="Assign session to this slot">
      <div className="space-y-4">
        <div>
          <label htmlFor="spanSlots" className="label">Session length (slots)</label>
          <select
            id="spanSlots"
            className="select w-full"
            value={spanSlots}
            onChange={(e) => setSpanSlots(Number(e.target.value))}
          >
            {Array.from({ length: maxSlots }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>{n} slot{n !== 1 ? 's' : ''}</option>
            ))}
          </select>
        </div>
        {sessions.length === 0 ? (
          <p className="text-gray-500">All sessions are already scheduled.</p>
        ) : (
          <ul className="space-y-2 max-h-[300px] overflow-y-auto">
            {sessions.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-4 border-b border-gray-100 pb-2">
                <span className="font-medium">{s.title}</span>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={assigning}
                  onClick={async () => {
                    setAssigning(true);
                    const effectiveSpan = (s.spanSlots != null && s.spanSlots >= 1 && s.spanSlots <= maxSlots)
                      ? s.spanSlots
                      : spanSlots;
                    await onAssign(s.id, effectiveSpan);
                    setAssigning(false);
                  }}
                >
                  Assign here
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

// ============================================
// Settings Tab
// ============================================

/** Inline-editable chip for a single option value. */
function EditableChip({
  value,
  onEdit,
  onRemove,
}: {
  value: string;
  onEdit: (oldValue: string, newValue: string) => void;
  onRemove: (value: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) {
      onEdit(value, trimmed);
    }
    setEditing(false);
    setDraft(trimmed || value);
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-primary-50 border border-primary-300 text-sm">
        <input
          ref={inputRef}
          type="text"
          className="bg-transparent border-none text-sm px-2 py-1 w-28 focus:outline-none focus:ring-0"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); }
            if (e.key === 'Escape') cancel();
          }}
          onBlur={commit}
        />
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-100 text-sm group">
      <button
        type="button"
        onClick={() => { setDraft(value); setEditing(true); }}
        className="hover:text-primary-700 cursor-text"
        title="Click to edit"
      >
        {value}
      </button>
      <button
        type="button"
        onClick={() => onRemove(value)}
        className="text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label={`Remove ${value}`}
      >
        ×
      </button>
    </span>
  );
}

function OptionsList({
  title,
  description,
  options,
  onAdd,
  onRemove,
  onEdit,
  newValue,
  onNewChange,
  onSave,
  saving,
  sameAsTemplate,
}: {
  title: string;
  description: string;
  options: string[];
  onAdd: (value: string) => void;
  onRemove: (value: string) => void;
  onEdit: (oldValue: string, newValue: string) => void;
  newValue: string;
  onNewChange: (value: string) => void;
  onSave: () => void;
  saving: boolean;
  sameAsTemplate: boolean;
}) {
  return (
    <div className="card">
      <h3 className="font-medium mb-2">{title}</h3>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <div className="flex flex-wrap gap-2 mb-3">
        {options.length === 0 && (
          <span className="text-sm text-gray-400 italic">No {title.toLowerCase()} defined yet.</span>
        )}
        {options.map((item) => (
          <EditableChip
            key={item}
            value={item}
            onEdit={onEdit}
            onRemove={onRemove}
          />
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          className="input flex-1"
          placeholder={`Add ${title.toLowerCase()}`}
          value={newValue}
          onChange={(e) => onNewChange(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), onAdd(newValue.trim()), onNewChange(''))}
        />
        <button type="button" onClick={() => { onAdd(newValue.trim()); onNewChange(''); }} className="btn btn-secondary">
          Add
        </button>
        <button type="button" onClick={onSave} className="btn btn-primary" disabled={saving || sameAsTemplate}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ============================================
// Attendees Tab
// ============================================

function AttendeesTab({ eventId }: { eventId: string }) {
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mergeSource, setMergeSource] = useState<string | null>(null);
  const [mergeTarget, setMergeTarget] = useState<string | null>(null);
  const [isMerging, setIsMerging] = useState(false);
  const [mergeResult, setMergeResult] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const loadAttendees = React.useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await fetchAttendees(eventId);
      setAttendees(data);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load attendees');
    } finally {
      setIsLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadAttendees();
  }, [loadAttendees]);

  const filtered = React.useMemo(() => {
    if (!searchQuery.trim()) return attendees;
    const q = searchQuery.toLowerCase();
    return attendees.filter(
      (a) =>
        a.nickname?.toLowerCase().includes(q) ||
        a.shortId.toLowerCase().includes(q) ||
        a.deviceId.toLowerCase().includes(q),
    );
  }, [attendees, searchQuery]);

  const handleMerge = async () => {
    if (!mergeSource || !mergeTarget) return;
    const source = attendees.find((a) => a.deviceId === mergeSource);
    const target = attendees.find((a) => a.deviceId === mergeTarget);
    if (!source || !target) return;

    if (
      !confirm(
        `Merge "${source.nickname}" (#${source.shortId}) into "${target.nickname}" (#${target.shortId})?\n\n` +
        `All proposals and votes from "${source.nickname}" will be reassigned to "${target.nickname}". This cannot be undone.`,
      )
    )
      return;

    setIsMerging(true);
    setMergeResult(null);
    try {
      const result = await mergeAttendees(
        eventId,
        mergeSource,
        mergeTarget,
        target.nickname,
      );
      setMergeResult(
        `Merged successfully: ${result.proposalsMerged} proposal(s) and ${result.votesMerged} vote(s) reassigned.`,
      );
      setMergeSource(null);
      setMergeTarget(null);
      await loadAttendees();
    } catch (e: any) {
      setMergeResult(`Error: ${e.message ?? 'Merge failed'}`);
    } finally {
      setIsMerging(false);
    }
  };

  const sourceAttendee = attendees.find((a) => a.deviceId === mergeSource);
  const targetAttendee = attendees.find((a) => a.deviceId === mergeTarget);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="inline-block w-6 h-6 border-4 border-primary-200 border-t-primary-400 rounded-full animate-spin" />
        <span className="ml-2 text-gray-500">Loading attendees...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button onClick={loadAttendees} className="btn btn-secondary btn-sm mt-2">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <h2 className="text-lg font-semibold">
          Attendees <span className="text-gray-400 font-normal text-sm">({attendees.length})</span>
        </h2>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Search by name or ID..."
            className="input text-sm w-56"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button onClick={loadAttendees} className="btn btn-secondary btn-sm" title="Refresh">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      {/* Merge section */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">
          <svg className="w-4 h-4 inline mr-1 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
          Merge Attendees
        </h3>
        <p className="text-xs text-gray-500 mb-3">
          Select two attendees below to merge them. The <strong>source</strong> identity will be absorbed into the <strong>target</strong>. All proposals and votes will be reassigned.
        </p>
        <div className="grid sm:grid-cols-[1fr_auto_1fr_auto] gap-2 items-end">
          <div>
            <label className="text-xs font-medium text-gray-600">Source (will be removed)</label>
            <select
              className="select text-sm mt-1"
              value={mergeSource ?? ''}
              onChange={(e) => setMergeSource(e.target.value || null)}
            >
              <option value="">Select source...</option>
              {attendees
                .filter((a) => a.deviceId !== mergeTarget)
                .map((a) => (
                  <option key={a.deviceId} value={a.deviceId}>
                    {a.nickname} (#{a.shortId}) – {a.proposalCount} card(s), {a.voteCount} vote(s)
                  </option>
                ))}
            </select>
          </div>
          <div className="flex items-center justify-center text-gray-400 font-bold text-lg pb-1">
            &rarr;
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Target (will be kept)</label>
            <select
              className="select text-sm mt-1"
              value={mergeTarget ?? ''}
              onChange={(e) => setMergeTarget(e.target.value || null)}
            >
              <option value="">Select target...</option>
              {attendees
                .filter((a) => a.deviceId !== mergeSource)
                .map((a) => (
                  <option key={a.deviceId} value={a.deviceId}>
                    {a.nickname} (#{a.shortId}) – {a.proposalCount} card(s), {a.voteCount} vote(s)
                  </option>
                ))}
            </select>
          </div>
          <button
            onClick={handleMerge}
            disabled={!mergeSource || !mergeTarget || isMerging}
            className="btn btn-primary btn-sm whitespace-nowrap"
          >
            {isMerging ? 'Merging...' : 'Merge'}
          </button>
        </div>
        {mergeSource && mergeTarget && sourceAttendee && targetAttendee && (
          <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
            <p className="text-yellow-800">
              <strong>{sourceAttendee.nickname}</strong> (#{sourceAttendee.shortId}) will be merged into{' '}
              <strong>{targetAttendee.nickname}</strong> (#{targetAttendee.shortId}).
              <br />
              {sourceAttendee.proposalCount} proposal(s) and {sourceAttendee.voteCount} vote(s) will be reassigned.
            </p>
          </div>
        )}
        {mergeResult && (
          <div
            className={`mt-3 rounded-lg p-3 text-sm ${
              mergeResult.startsWith('Error')
                ? 'bg-red-50 border border-red-200 text-red-700'
                : 'bg-green-50 border border-green-200 text-green-700'
            }`}
          >
            {mergeResult}
          </div>
        )}
      </div>

      {/* Attendees table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left font-semibold text-gray-700">Name</th>
              <th className="px-4 py-2 text-left font-semibold text-gray-700">Short ID</th>
              <th className="px-4 py-2 text-right font-semibold text-gray-700">Cards</th>
              <th className="px-4 py-2 text-right font-semibold text-gray-700">Votes</th>
              <th className="px-4 py-2 text-left font-semibold text-gray-700">First seen</th>
              <th className="px-4 py-2 text-left font-semibold text-gray-700 hidden lg:table-cell">Device ID</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  {searchQuery ? 'No attendees matching your search.' : 'No attendees have joined yet.'}
                </td>
              </tr>
            ) : (
              filtered.map((a) => (
                <tr
                  key={a.deviceId}
                  className={`hover:bg-gray-50 ${
                    a.deviceId === mergeSource
                      ? 'bg-red-50'
                      : a.deviceId === mergeTarget
                      ? 'bg-green-50'
                      : ''
                  }`}
                >
                  <td className="px-4 py-2 font-medium text-gray-900">{a.nickname || '(unnamed)'}</td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-500">#{a.shortId}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{a.proposalCount}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{a.voteCount}</td>
                  <td className="px-4 py-2 text-gray-500 whitespace-nowrap">
                    {a.firstSeen ? new Date(a.firstSeen).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '–'}
                  </td>
                  <td className="px-4 py-2 font-mono text-[10px] text-gray-400 hidden lg:table-cell truncate max-w-[200px]" title={a.deviceId}>
                    {a.deviceId}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const SLOT_DURATION_OPTIONS = [5, 10, 15, 20, 30, 45, 60];

function SettingsTab({
  eventId,
  event,
  template,
  onRefresh,
}: {
  eventId: string;
  event: { name: string; startDate: string; timezone: string; archivedAt: string | null; defaultSlotDurationMinutes?: number; votingOpen?: boolean; isActive?: boolean };
  template: Template | null;
  onRefresh: () => void;
}) {
  const [isArchiving, setIsArchiving] = useState(false);
  const [defaultSlotDurationMinutes, setDefaultSlotDurationMinutes] = useState(event?.defaultSlotDurationMinutes ?? 15);
  const [savingSlotDuration, setSavingSlotDuration] = useState(false);
  const [tagOptions, setTagOptions] = useState<string[]>(template?.tagOptions ?? []);
  const [languageOptions, setLanguageOptions] = useState<string[]>(template?.languageOptions ?? []);
  const [formatOptions, setFormatOptions] = useState<string[]>(template?.formatOptions ?? []);
  const [levelOptions, setLevelOptions] = useState<string[]>(template?.levelOptions ?? []);
  const [newTag, setNewTag] = useState('');
  const [newOption, setNewOption] = useState<{ lang: string; format: string; level: string }>({ lang: '', format: '', level: '' });
  const [savingOptions, setSavingOptions] = useState(false);
  /** Track renames so we can propagate them to existing proposals/sessions on save. */
  const [pendingRenames, setPendingRenames] = useState<Array<{ field: 'tags' | 'language' | 'format' | 'level'; oldValue: string; newValue: string }>>([]);

  // Event information editing state
  const [eventName, setEventName] = useState(event?.name ?? '');
  const [eventStartDate, setEventStartDate] = useState(event?.startDate ?? '');
  const [eventTimezone, setEventTimezone] = useState(event?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [savingEventInfo, setSavingEventInfo] = useState(false);
  const [isResettingOptions, setIsResettingOptions] = useState(false);

  React.useEffect(() => {
    setDefaultSlotDurationMinutes(event?.defaultSlotDurationMinutes ?? 15);
    setEventName(event?.name ?? '');
    setEventStartDate(event?.startDate ?? '');
    setEventTimezone(event?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
  }, [event?.defaultSlotDurationMinutes, event?.name, event?.startDate, event?.timezone]);

  React.useEffect(() => {
    setTagOptions(template?.tagOptions ?? []);
    setLanguageOptions(template?.languageOptions ?? []);
    setFormatOptions(template?.formatOptions ?? []);
    setLevelOptions(template?.levelOptions ?? []);
    setPendingRenames([]);
  }, [template?.tagOptions, template?.languageOptions, template?.formatOptions, template?.levelOptions]);

  const eventInfoChanged =
    eventName !== (event?.name ?? '') ||
    eventStartDate !== (event?.startDate ?? '') ||
    eventTimezone !== (event?.timezone ?? '');

  const handleSaveEventInfo = async () => {
    setSavingEventInfo(true);
    try {
      await updateEvent(eventId, {
        name: eventName,
        startDate: eventStartDate,
        timezone: eventTimezone,
      });
      onRefresh();
    } finally {
      setSavingEventInfo(false);
    }
  };

  const handleArchive = async () => {
    if (
      !confirm(
        'Archive this event? It will become read-only and preserved forever.'
      )
    ) {
      return;
    }

    setIsArchiving(true);
    try {
      await archiveEvent(eventId);
      window.location.reload();
    } finally {
      setIsArchiving(false);
    }
  };

  const handleAddTag = () => {
    const t = newTag.trim();
    if (t && !tagOptions.includes(t)) {
      setTagOptions([...tagOptions, t]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTagOptions(tagOptions.filter((x) => x !== tag));
  };

  const handleEditTag = (oldVal: string, newVal: string) => {
    if (tagOptions.includes(newVal)) return; // avoid duplicates
    setTagOptions(tagOptions.map((t) => (t === oldVal ? newVal : t)));
    setPendingRenames((r) => [...r, { field: 'tags', oldValue: oldVal, newValue: newVal }]);
  };

  /** Generic option editor for language / format / level lists. */
  const makeEditHandler = (
    field: 'language' | 'format' | 'level',
    options: string[],
    setOptions: React.Dispatch<React.SetStateAction<string[]>>,
  ) => (oldVal: string, newVal: string) => {
    if (options.includes(newVal)) return;
    setOptions(options.map((o) => (o === oldVal ? newVal : o)));
    setPendingRenames((r) => [...r, { field, oldValue: oldVal, newValue: newVal }]);
  };

  const handleSaveOptions = async () => {
    setSavingOptions(true);
    try {
      // 1. Propagate any renames to existing proposals, sessions, and rooms
      for (const rename of pendingRenames) {
        await renameTemplateOption(eventId, rename.field, rename.oldValue, rename.newValue);
      }

      // 2. Save the updated template options
      const current = await fetchTemplate(eventId);
      await updateTemplate(eventId, {
        ...(current || {}),
        tagOptions: tagOptions ?? [],
        languageOptions: languageOptions ?? [],
        formatOptions: formatOptions ?? [],
        levelOptions: levelOptions ?? [],
      });

      setPendingRenames([]);
      onRefresh();
    } finally {
      setSavingOptions(false);
    }
  };

  const handleResetOptions = async () => {
    if (!confirm('Reset all template options (tags, languages, formats, audience levels) to empty defaults? This cannot be undone.')) {
      return;
    }
    setIsResettingOptions(true);
    try {
      const current = await fetchTemplate(eventId);
      await updateTemplate(eventId, {
        ...(current || {}),
        tagOptions: [],
        languageOptions: [],
        formatOptions: [],
        levelOptions: [],
      });
      setTagOptions([]);
      setLanguageOptions([]);
      setFormatOptions([]);
      setLevelOptions([]);
      setNewTag('');
      setNewOption({ lang: '', format: '', level: '' });
      onRefresh();
    } finally {
      setIsResettingOptions(false);
    }
  };

  const sameAsTemplate =
    pendingRenames.length === 0 &&
    (template?.tagOptions ?? []).join() === tagOptions.join() &&
    (template?.languageOptions ?? []).join() === languageOptions.join() &&
    (template?.formatOptions ?? []).join() === formatOptions.join() &&
    (template?.levelOptions ?? []).join() === levelOptions.join();

  const hasAnyOptions =
    tagOptions.length > 0 || languageOptions.length > 0 || formatOptions.length > 0 || levelOptions.length > 0;

  return (
    <div className="space-y-6">
      {/* Event Information - Editable */}
      <div className="card">
        <h3 className="font-medium mb-2">Event Information</h3>
        {event.archivedAt ? (
          <>
            <p className="text-gray-600">{event.name}</p>
            <p className="text-sm text-yellow-600 mt-2">
              This event is archived (read-only).
            </p>
          </>
        ) : (
          <div className="space-y-4">
            <div>
              <label htmlFor="eventName" className="label">Event Name *</label>
              <input
                id="eventName"
                type="text"
                className="input"
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="eventStartDate" className="label">Start Date *</label>
                <input
                  id="eventStartDate"
                  type="date"
                  className="input"
                  value={eventStartDate}
                  onChange={(e) => setEventStartDate(e.target.value)}
                  required
                />
              </div>
              <div>
                <label htmlFor="eventTimezone" className="label">Timezone</label>
                <select
                  id="eventTimezone"
                  className="select"
                  value={eventTimezone}
                  onChange={(e) => setEventTimezone(e.target.value)}
                >
                  {COMMON_TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                className="btn btn-primary"
                disabled={savingEventInfo || !eventInfoChanged || !eventName.trim() || !eventStartDate}
                onClick={handleSaveEventInfo}
              >
                {savingEventInfo ? 'Saving...' : 'Save Event Info'}
              </button>
            </div>
          </div>
        )}
      </div>

      {!event.archivedAt && (
        <>
          {/* Active event toggle */}
          <ActiveEventCard eventId={eventId} isActive={!!event.isActive} onRefresh={onRefresh} />

          {/* Interest gate toggle */}
          <VotingToggleCard eventId={eventId} votingOpen={!!event.votingOpen} onRefresh={onRefresh} />

          <div className="card">
            <h3 className="font-medium mb-2">Schedule: default slot length</h3>
            <p className="text-sm text-gray-500 mb-3">
              One slot length in minutes. Time blocks are defined as start time + number of slots (e.g. 11:00 + 3 slots with 15 min = 11:00-11:45). Session cards can choose duration in multiples of this (15, 30, 45 min).
            </p>
            <div className="flex items-center gap-4">
              <select
                className="select"
                value={defaultSlotDurationMinutes}
                onChange={(e) => setDefaultSlotDurationMinutes(Number(e.target.value))}
              >
                {SLOT_DURATION_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n} minutes</option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-primary"
                disabled={savingSlotDuration || defaultSlotDurationMinutes === (event?.defaultSlotDurationMinutes ?? 15)}
                onClick={async () => {
                  setSavingSlotDuration(true);
                  try {
                    await updateEvent(eventId, { defaultSlotDurationMinutes });
                    onRefresh();
                  } finally {
                    setSavingSlotDuration(false);
                  }
                }}
              >
                {savingSlotDuration ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {pendingRenames.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
              <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-blue-800">
                  {pendingRenames.length} unsaved rename{pendingRenames.length !== 1 ? 's' : ''}
                </p>
                <ul className="text-xs text-blue-700 mt-1 list-disc list-inside">
                  {pendingRenames.map((r, i) => (
                    <li key={i}>{r.field}: "{r.oldValue}" &rarr; "{r.newValue}"</li>
                  ))}
                </ul>
                <p className="text-xs text-blue-600 mt-1">Click <strong>Save</strong> on any section below to apply renames to all existing session cards and sessions.</p>
              </div>
            </div>
          )}

          <div className="card">
            <h3 className="font-medium mb-2">Session Card Tags</h3>
            <p className="text-sm text-gray-500 mb-3">
              Pre-define tags that submitters can choose when creating session cards. Click a tag to edit it.
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              {tagOptions.length === 0 && (
                <span className="text-sm text-gray-400 italic">No tags defined yet.</span>
              )}
              {tagOptions.map((tag) => (
                <EditableChip
                  key={tag}
                  value={tag}
                  onEdit={handleEditTag}
                  onRemove={handleRemoveTag}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                className="input flex-1"
                placeholder="Add a tag"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
              />
              <button type="button" onClick={handleAddTag} className="btn btn-secondary">
                Add
              </button>
              <button
                type="button"
                onClick={handleSaveOptions}
                className="btn btn-primary"
                disabled={savingOptions || sameAsTemplate}
              >
                {savingOptions ? 'Saving...' : 'Save tags'}
              </button>
            </div>
          </div>

          <OptionsList
            title="Languages"
            description="Options for the Language field on session cards. Click an item to edit it."
            options={languageOptions}
            onAdd={(v) => v && !languageOptions.includes(v) && setLanguageOptions([...languageOptions, v])}
            onRemove={(v) => setLanguageOptions(languageOptions.filter((x) => x !== v))}
            onEdit={makeEditHandler('language', languageOptions, setLanguageOptions)}
            newValue={newOption.lang}
            onNewChange={(v) => setNewOption((o) => ({ ...o, lang: v }))}
            onSave={handleSaveOptions}
            saving={savingOptions}
            sameAsTemplate={sameAsTemplate}
          />
          <OptionsList
            title="Formats"
            description="Options for the Format field (e.g. Talk, Workshop). Click an item to edit it."
            options={formatOptions}
            onAdd={(v) => v && !formatOptions.includes(v) && setFormatOptions([...formatOptions, v])}
            onRemove={(v) => setFormatOptions(formatOptions.filter((x) => x !== v))}
            onEdit={makeEditHandler('format', formatOptions, setFormatOptions)}
            newValue={newOption.format}
            onNewChange={(v) => setNewOption((o) => ({ ...o, format: v }))}
            onSave={handleSaveOptions}
            saving={savingOptions}
            sameAsTemplate={sameAsTemplate}
          />
          <OptionsList
            title="Audience levels"
            description="Options for the Audience Level field. Click an item to edit it."
            options={levelOptions}
            onAdd={(v) => v && !levelOptions.includes(v) && setLevelOptions([...levelOptions, v])}
            onRemove={(v) => setLevelOptions(levelOptions.filter((x) => x !== v))}
            onEdit={makeEditHandler('level', levelOptions, setLevelOptions)}
            newValue={newOption.level}
            onNewChange={(v) => setNewOption((o) => ({ ...o, level: v }))}
            onSave={handleSaveOptions}
            saving={savingOptions}
            sameAsTemplate={sameAsTemplate}
          />

          {/* Reset to defaults */}
          {hasAnyOptions && (
            <div className="card border-yellow-200">
              <h3 className="font-medium text-yellow-700 mb-2">Reset Template Options</h3>
              <p className="text-sm text-gray-600 mb-4">
                Clear all tags, languages, formats, and audience levels back to empty defaults. Existing session cards will keep their selected values but submitters will no longer see dropdown options.
              </p>
              <button
                type="button"
                onClick={handleResetOptions}
                className="btn bg-yellow-100 text-yellow-800 hover:bg-yellow-200 focus:ring-yellow-500"
                disabled={isResettingOptions}
              >
                {isResettingOptions ? 'Resetting...' : 'Reset All Options to Defaults'}
              </button>
            </div>
          )}

          <div className="card border-red-200">
            <h3 className="font-medium text-red-700 mb-2">Danger Zone</h3>
            <p className="text-sm text-gray-600 mb-4">
              Archiving an event makes it read-only. This action cannot be undone.
            </p>
            <button
              onClick={handleArchive}
              className="btn btn-danger"
              disabled={isArchiving}
            >
              {isArchiving ? 'Archiving...' : 'Archive Event'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function VotingToggleCard({
  eventId,
  votingOpen,
  onRefresh,
}: {
  eventId: string;
  votingOpen: boolean;
  onRefresh: () => void;
}) {
  const [isToggling, setIsToggling] = useState(false);

  const handleToggle = async () => {
    setIsToggling(true);
    try {
      await toggleVoting(eventId, !votingOpen);
      onRefresh();
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <div className="card" style={votingOpen ? { border: '2px solid #22c55e' } : {}}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium mb-1">Attendee Interest</h3>
          <p className="text-sm text-gray-500">
            {votingOpen
              ? 'Interest is currently OPEN. Attendees can show interest in session cards during the presentation.'
              : 'Interest is currently CLOSED. Attendees cannot show interest until you open it (e.g. during the presentation).'}
          </p>
        </div>
        <button
          onClick={handleToggle}
          disabled={isToggling}
          className={`btn ${votingOpen ? 'btn-danger' : 'btn-primary'} flex-shrink-0`}
        >
          {isToggling ? 'Saving...' : votingOpen ? 'Close Interest' : 'Open Interest'}
        </button>
      </div>
      <div className="mt-2">
        <span className={`badge ${votingOpen ? 'badge-green' : 'badge-gray'}`}>
          {votingOpen ? 'Interest Open' : 'Interest Closed'}
        </span>
      </div>
    </div>
  );
}

const COMMON_TIMEZONES = [
  'Europe/Berlin',
  'Europe/Vienna',
  'Europe/Zurich',
  'Europe/London',
  'Europe/Paris',
  'Europe/Amsterdam',
  'Europe/Rome',
  'Europe/Madrid',
  'Europe/Stockholm',
  'Europe/Helsinki',
  'Europe/Warsaw',
  'Europe/Prague',
  'Europe/Budapest',
  'Europe/Bucharest',
  'Europe/Athens',
  'Europe/Istanbul',
  'Europe/Moscow',
  'US/Eastern',
  'US/Central',
  'US/Mountain',
  'US/Pacific',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Australia/Sydney',
  'Pacific/Auckland',
  'UTC',
];

function ActiveEventCard({
  eventId,
  isActive,
  onRefresh,
}: {
  eventId: string;
  isActive: boolean;
  onRefresh: () => void;
}) {
  const [isSaving, setIsSaving] = useState(false);

  const handleSetActive = async () => {
    setIsSaving(true);
    try {
      await apiSetActive(eventId);
      onRefresh();
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeactivate = async () => {
    setIsSaving(true);
    try {
      await apiDeactivate(eventId);
      onRefresh();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="card" style={isActive ? { border: '2px solid #22c55e' } : {}}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium mb-1">Active Event</h3>
          <p className="text-sm text-gray-500">
            {isActive
              ? 'This event is currently active. Attendees navigating to /sessionboard will see this event.'
              : 'This event is not the active event. Set it as active so attendees see it when visiting /sessionboard.'}
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {isActive ? (
            <button
              onClick={handleDeactivate}
              disabled={isSaving}
              className="btn btn-secondary"
            >
              {isSaving ? 'Saving...' : 'Deactivate'}
            </button>
          ) : (
            <button
              onClick={handleSetActive}
              disabled={isSaving}
              className="btn btn-primary"
            >
              {isSaving ? 'Saving...' : 'Set as Active'}
            </button>
          )}
        </div>
      </div>
      <div className="mt-2">
        <span className={`badge ${isActive ? 'badge-green' : 'badge-gray'}`}>
          {isActive ? 'Active' : 'Not Active'}
        </span>
      </div>
    </div>
  );
}
